import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Save, ArrowLeft } from "lucide-react";
import { faqApi } from "./adminApi";
import RichTextEditor from "./RichTextEditor";
import { isEmptyHtml } from "../utils/richText";
import "./Admin.css";
const emptyForm = {
  question: "",
  answer: "",
  order: "",
  isPublished: true,
};
function faqToForm(faq) {
  return {
    question: faq.question || "",
    answer: faq.answer || "",
    order: faq.order ?? 0,
    isPublished: faq.isPublished !== false,
  };
}
function formToPayload(form) {
  const payload = {
    question: form.question.trim(),
    answer: form.answer,
    isPublished: !!form.isPublished,
  };
  if (form.order !== "" && form.order !== null) payload.order = Number(form.order) || 0;
  return payload;
}
export default function FaqForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    async function load() {
      try {
        const data = await faqApi.listAll();
        const faq = data.find((f) => f._id === id);
        if (!faq) throw new Error("FAQ not found.");
        if (!cancelled) setForm(faqToForm(faq));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);
  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.question.trim()) {
      setError("Question is required.");
      return;
    }
    if (isEmptyHtml(form.answer)) {
      setError("Answer is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (isEdit) {
        await faqApi.update(id, payload);
      } else {
        await faqApi.create(payload);
      }
      navigate("/admin/faqs");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }
  if (loading) return <p>Loading…</p>;
  return (
    <div>
      <div className="admin-topbar">
        <h1>{isEdit ? "Edit FAQ" : "Add FAQ"}</h1>
        <Link to="/admin/faqs" className="admin-btn admin-btn--outline" style={{ width: "auto" }}>
          <ArrowLeft size={16} /> Back to FAQs
        </Link>
      </div>
      {error && <div className="admin-error">{error}</div>}
      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="admin-form__section">
          <h3>Question &amp; Answer</h3>
          <div className="admin-field admin-field--main">
            <label>Question *</label>
            <input
              value={form.question}
              onChange={(e) => update("question", e.target.value)}
              placeholder="e.g. Are the classes live or recorded?"
              maxLength={300}
              required
            />
          </div>
          <div className="admin-field admin-field--desc">
            <label>Answer *</label>
            <RichTextEditor
              mode="basic"
              value={form.answer}
              onChange={(html) => update("answer", html)}
              placeholder="Write the answer. Select text and click B to make it bold."
              minHeight={160}
            />
            <span className="admin-field__hint">
              Use the toolbar for bold, italic, underline, links and lists.
            </span>
          </div>
          <div className="admin-form__row">
            <div className="admin-field admin-field--main">
              <label>Display Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => update("order", e.target.value)}
                placeholder="Leave blank to add at the end"
              />
              <span className="admin-field__hint">Lower numbers show first on the website.</span>
            </div>
            <div className="admin-field admin-field--main">
              <label>Status</label>
              <div className="admin-toggle-row">
                <input
                  type="checkbox"
                  id="faqPublished"
                  checked={form.isPublished}
                  onChange={(e) => update("isPublished", e.target.checked)}
                />
                <label htmlFor="faqPublished" className="admin-toggle-row__label">
                  {form.isPublished ? "Published (visible on the live site)" : "Draft (hidden from the live site)"}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="admin-form__actions">
          <button type="submit" className="admin-btn admin-btn--primary" style={{ width: "auto" }} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save FAQ"}
          </button>
          <Link to="/admin/faqs" className="admin-btn admin-btn--outline">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}